/**
 * SubmittedTab Component
 * Shows cases submitted by employees or data entry (pending_qc status)
 * with search, client/entity/date filters, and pagination
 */
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, RefreshCw, Eye, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, User, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateDDMMYYYY } from '@/utils/textFormatting';

export default function SubmittedTab({ checkWisePending, onRefresh, onShowCaseDetail }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const submittedCases = useMemo(() => checkWisePending?.submitted_cases || [], [checkWisePending]);

  // Unique values for filters
  const uniqueClients = useMemo(() => [...new Set(submittedCases.map(c => c.client_name).filter(Boolean))].sort(), [submittedCases]);
  const uniqueEntities = useMemo(() => {
    let entities = submittedCases;
    if (clientFilter !== 'all') {
      entities = entities.filter(c => c.client_name === clientFilter);
    }
    return [...new Set(entities.map(c => c.entity_name).filter(Boolean))].sort();
  }, [submittedCases, clientFilter]);

  // Apply filters
  const filteredCases = useMemo(() => {
    let result = [...submittedCases];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.case_id?.toLowerCase().includes(search) ||
        c.employee_name?.toLowerCase().includes(search) ||
        c.employee_email?.toLowerCase().includes(search) ||
        c.client_name?.toLowerCase().includes(search) ||
        c.entity_name?.toLowerCase().includes(search)
      );
    }

    if (clientFilter !== 'all') {
      result = result.filter(c => c.client_name === clientFilter);
    }

    if (entityFilter !== 'all') {
      result = result.filter(c => c.entity_name === entityFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      result = result.filter(c => {
        if (!c.submitted_at) return false;
        const d = new Date(c.submitted_at);
        switch (dateFilter) {
          case 'today': return d >= today;
          case 'week': {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return d >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return d >= monthAgo;
          }
          default: return true;
        }
      });
    }

    return result;
  }, [submittedCases, searchTerm, clientFilter, entityFilter, dateFilter]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredCases.length / pageSize));
  const paginatedCases = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCases.slice(start, start + pageSize);
  }, [filteredCases, currentPage, pageSize]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, clientFilter, entityFilter, dateFilter, pageSize]);

  // Reset entity filter when client changes
  useEffect(() => { setEntityFilter('all'); }, [clientFilter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="submitted-tab-title">Submitted Cases</h1>
          <p className="text-slate-600">Cases submitted by employees or data entry team awaiting QC review</p>
        </div>
        <Button onClick={onRefresh} variant="outline" size="sm" data-testid="submitted-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, case ID, client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="submitted-search"
              />
            </div>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[180px]" data-testid="submitted-client-filter">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {uniqueClients.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-[180px]" data-testid="submitted-entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {uniqueEntities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[150px]" data-testid="submitted-date-filter">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Pagination (above table) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>Show</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span>of {filteredCases.length} cases</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}><ChevronsLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}><ChevronsRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Table */}
      {paginatedCases.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{searchTerm || clientFilter !== 'all' || entityFilter !== 'all' || dateFilter !== 'all' ? 'No cases match your filters' : 'No submitted cases pending QC review'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Case ID</TableHead>
                  <TableHead className="font-semibold">Candidate</TableHead>
                  <TableHead className="font-semibold">Client</TableHead>
                  <TableHead className="font-semibold">Entity</TableHead>
                  <TableHead className="font-semibold">Source</TableHead>
                  <TableHead className="font-semibold">Submitted Date</TableHead>
                  <TableHead className="font-semibold">Checks</TableHead>
                  <TableHead className="font-semibold text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCases.map((c) => (
                  <TableRow key={c.case_id} className="hover:bg-slate-50 cursor-pointer" data-testid={`submitted-row-${c.case_id}`}>
                    <TableCell className="font-mono text-sm text-blue-600">{c.case_id}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{c.employee_name}</p>
                        <p className="text-xs text-slate-400">{c.employee_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{c.client_name}</TableCell>
                    <TableCell className="text-sm">{c.entity_name}</TableCell>
                    <TableCell>
                      <Badge variant={c.submission_source === 'Employee' ? 'default' : 'secondary'} className={c.submission_source === 'Employee' ? 'bg-blue-500' : 'bg-slate-500'}>
                        {c.submission_source}
                      </Badge>
                      {c.basic_promoted && (
                        <Badge variant="outline" className="ml-1 text-xs border-amber-300 text-amber-600">Basic First</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        {formatDateDDMMYYYY(c.submitted_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{c.total_checks}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onShowCaseDetail && onShowCaseDetail(c.case_id)}
                        data-testid={`submitted-view-${c.case_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
